// src/pages/BlockedList.tsx
import React, { useEffect, useState } from "react";
import api from "../../../shared/api/axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../../shared/api/axios";

type BlockedItem = {
  _id: string;
  profilename: string;
  picture: string;
  isDeactivated: boolean;
  isDeleted: boolean;
  since: string;
};

const pageWrap: React.CSSProperties = {
  display: "flex",
  minHeight: "100vh",
  background: "#020617", // dark slate
  color: "#e5e7eb",
};

const mainStyle: React.CSSProperties = {
  padding: 24,
  width: "100%",
  boxSizing: "border-box",
};

const innerWrap: React.CSSProperties = {
  maxWidth: 860,
  margin: "0 auto",
};

const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,
};

const rowCard: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 12,
  background: "#020617",
  borderRadius: 12,
  border: "1px solid #1e293b",
  cursor: "pointer",
};

const avatarStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 8,
  backgroundColor: "rgb(38, 29, 162)", // same blue as elsewhere
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 22,
  color: "#fff",
  flexShrink: 0,
};

const pill: React.CSSProperties = {
  fontSize: 12,
  padding: "2px 8px",
  borderRadius: 999,
  border: "1px solid",
  whiteSpace: "nowrap",
};

const btn: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid #1f2937",
  background: "#020617",
  color: "#e5e7eb",
  cursor: "pointer",
};

const dangerBtn: React.CSSProperties = {
  ...btn,
  background: "#7f1d1d",
  borderColor: "#b91c1c",
};

const BlockedList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
const [confirmTarget, setConfirmTarget] = useState<BlockedItem | null>(null);

  const [items, setItems] = useState<BlockedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/block-report/block/list");
      setItems(data?.items || []);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.msg || t("blockedList.errorLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

 const handleUnblock = (user: BlockedItem) => {
  setConfirmTarget(user);
};


  const handleRowClick = (id: string) => {
    // same visitor profile route as chat & likes modal
    navigate(`/visitor-profile/${id}`);
  };

  return (
    <div style={pageWrap}>
      <main style={mainStyle}>
        <div style={innerWrap}>
          <header style={headerRow}>
            <h2 style={{ margin: 0, color: "#e5e7eb" }}>{t("blockedList.title")}</h2>
            <button onClick={load} style={btn} disabled={loading}>
              {loading ? t("blockedList.refreshing") : t("blockedList.refresh")}
            </button>
          </header>

          {error && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fee2e2",
                border: "1px solid #b91c1c",
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: 16, color: "#9ca3af" }}>
              {t("blockedList.loading")}
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 16, color: "#9ca3af" }}>
              {t("blockedList.empty")}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {items.map((u) => (
                <div
                  key={u._id}
                  style={rowCard}
                  onClick={() => handleRowClick(u._id)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleRowClick(u._id)}
                >
                  {/* Always default ghost avatar */}
                  <div style={avatarStyle}>👻</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <strong style={{ fontSize: 16, color: "#e5e7eb" }}>
                        {u.profilename}
                      </strong>
                      {u.isDeleted && (
                        <span
                          style={{
                            ...pill,
                            color: "#9ca3af",
                            borderColor: "#4b5563",
                            background: "#020617",
                          }}
                        >
                          {t("blockedList.statusDeleted")}
                        </span>
                      )}
                      {!u.isDeleted && u.isDeactivated && (
                        <span
                          style={{
                            ...pill,
                            color: "#fbbf24",
                            borderColor: "#f59e0b",
                            background: "#451a03",
                          }}
                        >
                          {t("blockedList.statusDeactivated")}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        color: "#9ca3af",
                        fontSize: 13,
                        marginTop: 4,
                      }}
                    >
                      {t("blockedList.blockedSince")}{" "}
                      {u.since ? new Date(u.since).toLocaleString() : "—"}
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // don’t navigate when clicking unblock
                      handleUnblock(u);
                    }}
                    style={dangerBtn}
                    disabled={unblocking === u._id}
                  >
                    {unblocking === u._id
                      ? t("blockedList.unblocking")
                      : t("blockedList.unblock")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      {confirmTarget && (
  <div style={confirmOverlay}>
    <div style={confirmCard}>
      <div style={{ color: "#e5e7eb", marginBottom: 12, fontSize: 15 }}>
        {t("blockedList.confirmUnblock")}
        <br />
        <strong>@{confirmTarget.profilename}</strong>
      </div>

      <button
        style={primaryBtn}
        onClick={async () => {
          try {
            setUnblocking(confirmTarget._id);
            await api.post(`/block-report/unblock/${confirmTarget._id}`, {});
            setItems((prev) =>
              prev.filter((i) => i._id !== confirmTarget._id)
            );
          } catch (e: any) {
            alert(e?.response?.data?.msg || t("blockedList.errorUnblock"));
          } finally {
            setUnblocking(null);
            setConfirmTarget(null);
          }
        }}
      >
        ✅ {t("common.confirm")}
      </button>

      <button style={cancelBtn} onClick={() => setConfirmTarget(null)}>
        ❌ {t("common.cancel")}
      </button>
    </div>
  </div>
)}

    </div>
  );
};

export default BlockedList;

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#020617",
  borderRadius: 16,
  padding: 20,
  border: "1px solid #1e293b",
  textAlign: "center",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 10,
  border: "none",
  background: "#5b21b6",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 10,
  border: "none",
  background: "transparent",
  color: "#9ca3af",
};
